import React, { useCallback, useEffect, useRef, useState } from "react";
import { cardLabel, filmstripCell, filmstripGrid } from "@veyso/core";
import type { AudioRole, AudioTrack, FocusEffect, FocusMode, TimelineLayout } from "@veyso/core";
import { sourceSecondsAt } from "@veyso/timeline";
import { collidingVoiceIds, packRows, voiceLaneRows } from "./voiceLanes.js";
import type { Timeline } from "@veyso/core";
import { timecode } from "../components/ui.js";
import { rulerMinWidth, rulerStep } from "./ruler.js";
import type { Filmstrip } from "./SyncPoint.js";

export interface TimelineTrackProps {
  timeline: Timeline;
  layout: TimelineLayout;
  frame: number;
  onSeek: (frame: number) => void;
  selectedActionId: string | null;
  onSelectAction: (actionId: string | null) => void;
  onSelectSection: (section: "head" | "body" | "end") => void;
  /** Opens the transitions pane, from the marker drawn at each join. */
  onSelectTransitions: () => void;
  /** The effect being edited, which is a different thing from the action it came from. */
  selectedFocusId: string | null;
  onSelectFocus: (id: string | null) => void;
  /** Committed on pointer-up, in body seconds. */
  onMoveFocus: (id: string, start: number, duration: number) => void;
  /**
   * Nudging a HEAD or END narration clip, in seconds, relative to where it sits
   * now. Committed on pointer-up.
   *
   * Only the bookends are draggable here. A body line's position is derived from
   * the moment of the recording it describes, and it is held on the script — so
   * moving one is a pause on that line, not a free position on this track.
   */
  onNudgeBookendVoice: (which: "head" | "end", deltaSeconds: number) => void;
  activeSection: string;
  /**
   * The project's own frame rate, which is what gets encoded. Positions come
   * from `layout`, whose rate can be lower while the preview is running at half
   * rate — labelling that here would advertise the preview's rate as the
   * project's.
   */
  renderFps: number;
  /** The recording's contact sheet, drawn inside the video clip. */
  filmstrip: Filmstrip | null;
}

/** Lane heights. Video is tallest because it is the lane you actually edit in. */
const RULER_H = 14;
const EFFECTS_H = 22;
const CARDS_H = 22;
const VIDEO_H = 52;
const VOICE_ROW_H = 22;
const GAP = 3;

/** Shorter than this an effect is a flicker, so a drag cannot make one. */
const MIN_EFFECT = 0.2;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** Which section's properties a voice clip belongs to. */
const SECTION_FOR_ROLE: Record<AudioRole, "head" | "body" | "end"> = {
  "head-voice": "head",
  "body-voice": "body",
  "end-voice": "end",
  original: "body",
  music: "body",
};

const EFFECT_NAMES: Record<FocusMode, string> = {
  zoom: "Zoom",
  highlight: "Highlight",
  callout: "Callout",
  cursor: "Cursor",
  blur: "Blur",
};

function trackLabel(track: AudioTrack, narrationIndex: number): string {
  if (track.role === "original") return "Original audio";
  if (track.role === "music") return "Music";
  if (track.role === "head-voice") return "HEAD narration";
  if (track.role === "end-voice") return "END narration";
  return `Narration ${narrationIndex}`;
}

/** Measures the track in pixels, which the filmstrip needs to size its cells. */
function useTrackWidth(ref: React.RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    setWidth(element.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * The recording's own frames, tiled across the body clip.
 *
 * Cells are sized by the lane's *height* and laid end to end at their natural
 * aspect, the way a filmstrip actually looks. Sizing them by dividing the clip's
 * width instead produces cells a few pixels tall sitting in a mostly empty lane,
 * because the frame's shape has nothing to do with how much horizontal room each
 * one happens to get.
 *
 * Memoised, and kept out of the parent, because the strip re-renders whenever
 * the playhead moves otherwise — seventy background-positioned elements
 * recomputed sixty times a second, for a picture that never changes.
 */
const FilmstripCells = React.memo<{
  filmstrip: Filmstrip | null;
  bodySeconds: number;
  bodyPixels: number;
  /** Ranges of the recording that survived the cut, in source seconds. */
  segments: readonly { start: number; end: number }[];
}>(({ filmstrip, bodySeconds, bodyPixels, segments }) => {
  if (!filmstrip || bodyPixels <= 0) return null;

  // The sheet's own grid, from the duration it was built for — not from the
  // body. Those are the same number only on an uncut recording; after a cut
  // they diverge, and computing the grid from the body gave the wrong row
  // count, so the wrong cell height, so the wrong aspect. On a 393.6s
  // recording cut to 133s it drew 58 frames at 31px where 21 at 89px belong.
  const grid = filmstripGrid(filmstrip.durationSeconds || bodySeconds);
  const aspect = filmstrip.width / grid.columns / (filmstrip.height / grid.rows);
  const drawHeight = VIDEO_H - 2;
  const drawWidth = drawHeight * aspect;
  const count = Math.ceil(bodyPixels / drawWidth);
  // A strip narrower than a couple of frames is noise, not information.
  if (count < 2) return null;

  return (
    <>
      {Array.from({ length: count }, (_, index) => {
        const atBody = ((index * drawWidth) / bodyPixels) * bodySeconds;
        // The sheet is laid out in source seconds and this lane is body
        // seconds, so every frame is looked up through the cut rather than
        // straight across — otherwise a cut timeline shows frames from
        // moments that are no longer in the video.
        const seconds = sourceSecondsAt(atBody, segments);
        const cell = filmstripCell(
          seconds,
          filmstrip.durationSeconds || bodySeconds,
          filmstrip.width,
          filmstrip.height,
          drawWidth,
        );
        return (
          <span
            key={index}
            aria-hidden
            className="absolute top-0 bg-no-repeat opacity-80"
            style={{
              left: index * drawWidth,
              width: Math.ceil(drawWidth),
              height: drawHeight,
              backgroundImage: `url(/assets/${filmstrip.key})`,
              backgroundSize: cell.backgroundSize,
              backgroundPosition: cell.backgroundPosition,
            }}
          />
        );
      })}
    </>
  );
});
FilmstripCells.displayName = "FilmstripCells";

/**
 * Four lanes over one clock: effects, cards, video, voice.
 *
 * Lane positions come from `computeLayout`, the same function the renderer uses,
 * so this is a true map of the output rather than a decorative approximation.
 *
 * The video lane draws the recording's own frames. A flat coloured block tells
 * you a video exists; frames tell you *where you are in it*, which is the thing
 * you need when deciding where a zoom belongs or which moment a line of
 * narration is describing.
 */
export const TimelineTrack: React.FC<TimelineTrackProps> = ({
  timeline,
  layout,
  frame,
  onSeek,
  selectedActionId,
  onSelectAction,
  onSelectSection,
  onSelectTransitions,
  selectedFocusId,
  onSelectFocus,
  onMoveFocus,
  onNudgeBookendVoice,
  activeSection,
  renderFps,
  filmstrip,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackWidth = useTrackWidth(trackRef);
  const bodySeconds = timeline.sections.body.duration;
  const total = Math.max(1, layout.totalFrames);
  const pct = (frames: number) => `${(frames / total) * 100}%`;

  const seekFromPointer = useCallback(
    (clientX: number) => {
      const bounds = trackRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const ratio = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
      onSeek(Math.round(ratio * (total - 1)));
    },
    [onSeek, total],
  );

  /**
   * Dragging one effect: its position, or either of its ends.
   *
   * Committed on pointer-up rather than on every move, so a drag is one undo
   * step instead of two hundred. The pointer is captured, so leaving the track
   * mid-drag keeps working the way it does in every other editor.
   */
  const dragEffect = useCallback(
    (event: React.PointerEvent, effect: FocusEffect, edge: "move" | "start" | "end") => {
      const bounds = trackRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width <= 0) return;

      const bodySeconds = layout.body.durationInFrames / layout.fps;
      const perPixel = total / layout.fps / bounds.width;
      const originX = event.clientX;
      const { start, duration } = effect;
      let next = { start, duration };

      const onMove = (move: PointerEvent) => {
        const delta = (move.clientX - originX) * perPixel;
        if (edge === "move") {
          next = { start: clamp(start + delta, 0, bodySeconds - duration), duration };
        } else if (edge === "start") {
          // Trimming the front moves the start and shortens by the same amount,
          // so the far end stays where the eye left it.
          const limit = start + duration - MIN_EFFECT;
          const at = clamp(start + delta, 0, limit);
          next = { start: at, duration: start + duration - at };
        } else {
          next = { start, duration: clamp(duration + delta, MIN_EFFECT, bodySeconds - start) };
        }
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (Math.abs(next.start - start) > 0.01 || Math.abs(next.duration - duration) > 0.01) {
          onMoveFocus(effect.id, Number(next.start.toFixed(3)), Number(next.duration.toFixed(3)));
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [layout.body.durationInFrames, layout.fps, total, onMoveFocus],
  );

  /**
   * Dragging a bookend's narration along the track.
   *
   * Committed as a delta rather than an absolute position: the offset it writes
   * is relative to a placement the builder recomputes, so sending "where it
   * ended up" would bake in whatever the automatic position happened to be that
   * day. The builder clamps it back inside the card, so a drag can overshoot
   * without the caller having to know the bounds.
   */
  const dragVoice = useCallback(
    (event: React.PointerEvent, which: "head" | "end") => {
      // The track itself seeks on pointer-down. Without this, grabbing a clip
      // would also throw the playhead to wherever you grabbed it.
      event.stopPropagation();
      const bounds = trackRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width <= 0) return;
      const perPixel = total / layout.fps / bounds.width;
      const originX = event.clientX;
      let delta = 0;

      const onMove = (move: PointerEvent) => {
        delta = (move.clientX - originX) * perPixel;
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (Math.abs(delta) > 0.02) onNudgeBookendVoice(which, Number(delta.toFixed(3)));
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [layout.fps, total, onNudgeBookendVoice],
  );

  // Ticks every 15 seconds, or every 5 on a short video. The track is never
  // narrower than those labels need; past that it scrolls. See `ruler.ts`.
  const tickSeconds = rulerStep(layout.totalFrames / layout.fps);
  const tickCount = Math.floor(layout.totalFrames / layout.fps / tickSeconds);
  const minTrackWidth = rulerMinWidth(layout.totalFrames / layout.fps);

  /**
   * Keeps the playhead in view once the track is wider than the window.
   *
   * Only when it has left the visible part, so scrolling away to look at
   * something while paused is not undone — the frame does not change then.
   * It lands a little in from the left edge, so playback has somewhere to go.
   */
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || trackWidth <= scroller.clientWidth) return;
    const x = (frame / total) * trackWidth;
    if (x < scroller.scrollLeft || x > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollLeft = Math.max(0, x - scroller.clientWidth * 0.1);
    }
  }, [frame, total, trackWidth]);

  const effectRows = packRows(timeline.sections.body.focus, (effect) => ({
    start: effect.start,
    end: effect.start + effect.duration,
  }));
  const voiceRows = voiceLaneRows(timeline.audio);
  const collidingVoice = collidingVoiceIds(timeline.audio);

  const laneHeight = {
    effects: Math.max(1, effectRows.length) * EFFECTS_H + Math.max(0, effectRows.length - 1) * GAP,
    cards: CARDS_H,
    video: VIDEO_H,
    voice: Math.max(1, voiceRows.length) * VOICE_ROW_H + Math.max(0, voiceRows.length - 1) * GAP,
  };
  const laneTop = {
    effects: RULER_H + GAP,
    cards: RULER_H + GAP + laneHeight.effects + GAP,
    video: RULER_H + GAP + laneHeight.effects + GAP + laneHeight.cards + GAP,
    voice: RULER_H + GAP + laneHeight.effects + GAP + laneHeight.cards + GAP + laneHeight.video + GAP,
  };
  const totalHeight = laneTop.voice + laneHeight.voice;

  const bodyPixels = (layout.body.durationInFrames / total) * trackWidth;

  const lane = (top: number, height: number) => (
    <div className="absolute left-0 right-0 rounded-[5px] bg-bay-800" style={{ top, height }} aria-hidden />
  );

  const clip = (
    key: string,
    top: number,
    height: number,
    from: number,
    width: number,
    label: string,
    detail: string,
    tone: "card" | "video" | "voice" | "muted" | "effect" | "clash",
    active: boolean,
    onClick: () => void,
    children?: React.ReactNode,
    onDragStart?: (event: React.PointerEvent) => void,
  ) => {
    const tones = {
      card: "border-[var(--edge-strong)] bg-bay-700 hover:bg-bay-600 text-ink-100",
      video: "border-scope-600/60 bg-scope-600/25 hover:bg-scope-600/35 text-ink-100",
      voice: "border-signal-ok/45 bg-signal-ok/15 hover:bg-signal-ok/25 text-ink-100",
      effect: "border-ember-600/50 bg-ember-600/25 hover:bg-ember-600/40 text-ink-100",
      // Kept on the timeline so you can see what was replaced, drawn as an
      // outline because it makes no sound.
      muted: "border-dashed border-[var(--edge-strong)] bg-transparent hover:bg-bay-800 text-ink-400",
      // Two voices at the same instant. Not the same as selected — this is the
      // clip telling you something is wrong, not the editor telling you where
      // you are.
      clash: "border-signal-bad/70 bg-signal-bad/20 hover:bg-signal-bad/30 text-ink-100",
    };

    // A clip narrower than a word shows no text at all. Truncation turns
    // "Narration 3" into "N.." and "END" into "E", which reads as a rendering
    // fault rather than as a short clip; the tooltip still names it.
    const roomy = (width / total) * 100 >= 4;

    return (
      <button
        key={key}
        title={detail ? `${label} — ${detail}` : label}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
        data-active={active}
        onPointerDown={onDragStart}
        style={{ top, height, left: pct(from), width: pct(width), cursor: onDragStart ? "ew-resize" : undefined }}
        className={`absolute flex flex-col justify-center overflow-hidden rounded-[5px] border text-left transition-colors ${roomy ? "px-2" : "px-0"} ${tones[tone]} data-[active=true]:border-ember-500 data-[active=true]:ring-1 data-[active=true]:ring-ember-500/40`}
      >
        {children}
        {roomy ? <span className="relative truncate text-[10px] font-semibold leading-tight">{label}</span> : null}
        {roomy && detail && height > 34 ? (
          <span className="relative tnum truncate text-[10px] leading-tight text-ink-300">{detail}</span>
        ) : null}
      </button>
    );
  };

  // Numbered by when they are heard, not by which sub-row they happened to be
  // packed into.
  const narrationNumber = new Map(
    timeline.audio
      .filter((track) => track.role === "body-voice")
      .sort((a, b) => a.startAt - b.startAt)
      .map((track, index) => [track.id, index + 1]),
  );

  return (
    <div className="shrink-0 border-t border-[var(--edge)] px-4 pb-4 pt-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="eyebrow">Timeline</span>
        <span className="tnum text-[11px] text-ink-400">
          {renderFps} fps · {layout.width}×{layout.height}
        </span>
      </div>

      <div className="flex gap-2">
        {/* Lane gutter. Labels sit outside the clock so no clip has to carry
            the lane's name as well as its own. */}
        <div className="relative w-[46px] shrink-0" style={{ height: totalHeight }}>
          {(["effects", "cards", "video", "voice"] as const).map((name) => (
            <span
              key={name}
              className="eyebrow absolute right-0 !text-[9px] leading-none text-ink-400"
              style={{ top: laneTop[name] + laneHeight[name] / 2 - 4 }}
            >
              {name}
            </span>
          ))}
        </div>

        {/* Scrolls sideways when the video is too long for the window. The
            padding is room for the first and last labels, which centre on
            their tick and would otherwise be cut in half at the edges. */}
        <div ref={scrollRef} className="-ml-5 -mr-4 min-w-0 flex-1 overflow-x-auto pl-5 pr-4">
          <div
            ref={trackRef}
            onPointerDown={(event) => {
              // Clicking a clip selects it; only the empty track scrubs.
              if ((event.target as HTMLElement).closest("button")) return;
              seekFromPointer(event.clientX);
              const onMove = (move: PointerEvent) => seekFromPointer(move.clientX);
              const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
              };
              window.addEventListener("pointermove", onMove);
              window.addEventListener("pointerup", onUp);
            }}
            className="relative cursor-pointer"
            style={{ height: totalHeight, width: `max(100%, ${minTrackWidth}px)` }}
          >
            {Array.from({ length: tickCount + 1 }, (_, index) => {
              const seconds = index * tickSeconds;
              return (
                <span
                  key={seconds}
                  className="tnum absolute top-0 -translate-x-1/2 text-[10px] text-ink-400"
                  style={{ left: pct(seconds * layout.fps) }}
                >
                  {timecode(seconds)}
                </span>
              );
            })}

            {lane(laneTop.effects, laneHeight.effects)}
            {lane(laneTop.cards, laneHeight.cards)}
            {lane(laneTop.video, laneHeight.video)}
            {lane(laneTop.voice, laneHeight.voice)}

            {/* Effects ------------------------------------------------------ */}
            {effectRows.flatMap((row, rowIndex) =>
              row.map((effect) => {
                const action = effect.actionId
                  ? timeline.sections.body.actions.find((candidate) => candidate.id === effect.actionId)
                  : undefined;
                const name = EFFECT_NAMES[effect.mode];
                const subject = effect.label ?? action?.label;
                const selected = effect.id === selectedFocusId;
                const roomy = ((effect.duration * layout.fps) / total) * 100 >= 4;

                return (
                  <button
                    key={effect.id}
                    title={`${subject ? `${name}: ${subject}` : name} — ${effect.start.toFixed(1)}s for ${effect.duration.toFixed(1)}s. Drag to move, drag an edge to trim.`}
                    data-active={selected}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      onSelectFocus(effect.id);
                      if (effect.actionId) onSelectAction(effect.actionId);
                      dragEffect(event, effect, "move");
                    }}
                    style={{
                      top: laneTop.effects + rowIndex * (EFFECTS_H + GAP),
                      height: EFFECTS_H,
                      left: pct(layout.body.from + effect.start * layout.fps),
                      width: pct(Math.max(1, effect.duration * layout.fps)),
                    }}
                    className={`absolute flex cursor-grab items-center overflow-hidden rounded-[5px] border text-left transition-colors active:cursor-grabbing ${
                      roomy ? "px-2" : "px-0"
                    } ${
                      effect.actionId
                        ? "border-ember-600/50 bg-ember-600/25 hover:bg-ember-600/40"
                        : // Dashed marks an effect that is the user's own rather
                          // than one seeded from a detected action.
                          "border-dashed border-ember-600/70 bg-ember-600/15 hover:bg-ember-600/30"
                    } text-ink-100 data-[active=true]:border-ember-500 data-[active=true]:ring-1 data-[active=true]:ring-ember-500/40`}
                  >
                    {roomy ? (
                      <span className="truncate text-[10px] font-semibold leading-tight">
                        {subject ? `${name}: ${subject}` : name}
                      </span>
                    ) : null}

                    {/* Trim handles, only on the selected one — every clip
                        wearing grab bars is a lane you cannot read. */}
                    {selected ? (
                      <>
                        <span
                          onPointerDown={(event) => {
                            event.stopPropagation();
                            dragEffect(event, effect, "start");
                          }}
                          className="absolute inset-y-0 left-0 w-[6px] cursor-ew-resize rounded-l-[4px] bg-ember-500"
                        />
                        <span
                          onPointerDown={(event) => {
                            event.stopPropagation();
                            dragEffect(event, effect, "end");
                          }}
                          className="absolute inset-y-0 right-0 w-[6px] cursor-ew-resize rounded-r-[4px] bg-ember-500"
                        />
                      </>
                    ) : null}
                  </button>
                );
              }),
            )}

            {timeline.sections.body.focus.length === 0 ? (
              <span
                className="pointer-events-none absolute left-2 text-[10px] italic text-ink-400"
                style={{ top: laneTop.effects + 5 }}
              >
                No zooms yet — they come from what the recorder captured
              </span>
            ) : null}

            {/* Cards -------------------------------------------------------- */}
            {layout.head
              ? clip(
                  "head",
                  laneTop.cards,
                  CARDS_H,
                  layout.head.from,
                  layout.head.durationInFrames,
                  (timeline.sections.head && cardLabel(timeline.sections.head.content)) || "Title card",
                  "",
                  "card",
                  activeSection.toLowerCase() === "head",
                  () => onSelectSection("head"),
                )
              : null}

            {layout.end
              ? clip(
                  "end",
                  laneTop.cards,
                  CARDS_H,
                  layout.end.from,
                  layout.end.durationInFrames,
                  (timeline.sections.end && cardLabel(timeline.sections.end.content)) || "End card",
                  "",
                  "card",
                  activeSection.toLowerCase() === "end",
                  () => onSelectSection("end"),
                )
              : null}

            {/* Transitions.
                A transition is a stretch where two sections are both on screen,
                and nothing on the timeline said so — the clips simply abutted, so
                a 0.8s crossfade and a hard cut drew identically. The band marks
                the overlap and the bowtie marks its centre, which is the part
                that stays visible once the overlap is a pixel wide on a long
                recording. */}
            {[
              ["headToBody", layout.headToBody, timeline.transitions.headToBody] as const,
              ["bodyToEnd", layout.bodyToEnd, timeline.transitions.bodyToEnd] as const,
            ].map(([key, span, transition]) =>
              span && transition ? (
                <button
                  key={key}
                  title={`${key === "headToBody" ? "HEAD → BODY" : "BODY → END"}: ${transition.type.replace(/-/g, " ")}, ${transition.duration.toFixed(2)}s — click to edit`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectTransitions();
                  }}
                  data-active={activeSection === "TRANSITIONS"}
                  style={{
                    top: laneTop.cards,
                    height: laneTop.video + laneHeight.video - laneTop.cards,
                    left: pct(span.from + span.durationInFrames / 2),
                  }}
                  className="group absolute z-10 flex -translate-x-1/2 items-center justify-center"
                >
                  {/* Sized in frames so it grows with the overlap, but never
                      narrower than the glyph it sits behind. */}
                  <span
                    className="absolute inset-y-0 left-1/2 -translate-x-1/2 rounded-[3px] border-x border-ember-400/50 opacity-70 group-hover:opacity-100 group-data-[active=true]:opacity-100"
                    style={{
                      width: `max(14px, ${(span.durationInFrames / total) * 100}%)`,
                      background: "repeating-linear-gradient(115deg, rgba(245,148,41,0.38) 0 2px, transparent 2px 5px)",
                    }}
                  />
                  <span className="relative flex h-[14px] w-[14px] items-center justify-center rounded-full border border-ember-400/70 bg-bay-900 text-ember-400 group-hover:bg-bay-800">
                    <svg viewBox="0 0 12 12" className="h-[8px] w-[8px]" fill="currentColor" aria-hidden>
                      <path d="M1 1 L5.4 6 L1 11 Z M11 1 L6.6 6 L11 11 Z" />
                    </svg>
                  </span>
                </button>
              ) : null,
            )}

            {!layout.head && !layout.end ? (
              <span
                className="pointer-events-none absolute left-2 text-[10px] italic text-ink-400"
                style={{ top: laneTop.cards + 5 }}
              >
                No title or end card
              </span>
            ) : null}

            {/* Video -------------------------------------------------------- */}
            {clip(
              "body",
              laneTop.video,
              VIDEO_H,
              layout.body.from,
              layout.body.durationInFrames,
              "Recording",
              `${(layout.body.durationInFrames / layout.fps).toFixed(1)}s · ${timeline.sections.body.actions.length} actions`,
              "video",
              activeSection.toLowerCase() === "body",
              () => onSelectSection("body"),
              <>
                <FilmstripCells
                  filmstrip={filmstrip}
                  bodySeconds={bodySeconds}
                  bodyPixels={bodyPixels}
                  segments={timeline.sections.body.segments}
                />
                {/* Just enough shading under the label to keep it readable; the
                    rest of the strip stays clear. */}
                <span className="pointer-events-none absolute left-0 top-0 h-full w-[190px] bg-gradient-to-r from-bay-850/95 to-transparent" />
              </>,
            )}

            {/* Action markers: the thing you click to edit a moment. */}
            {timeline.sections.body.actions.map((action) => (
              <button
                key={action.id}
                title={action.label ?? action.type}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectAction(action.id === selectedActionId ? null : action.id);
                  onSeek(Math.round(layout.body.from + action.timestamp * layout.fps));
                }}
                className={`absolute h-[9px] w-[9px] -translate-x-1/2 rotate-45 rounded-[2px] border transition-colors after:absolute after:-inset-[8px] after:content-[''] ${
                  action.id === selectedActionId
                    ? "border-ember-400 bg-ember-400"
                    : "border-scope-300/70 bg-scope-400 hover:bg-scope-300"
                }`}
                style={{
                  top: laneTop.video + VIDEO_H - 6,
                  left: pct(layout.body.from + action.timestamp * layout.fps),
                }}
              />
            ))}

            {/* Voice -------------------------------------------------------- */}
            {voiceRows.flatMap((row, rowIndex) =>
              row.map((track) =>
                clip(
                  track.id,
                  laneTop.voice + rowIndex * (VOICE_ROW_H + GAP),
                  VOICE_ROW_H,
                  track.startAt * layout.fps,
                  Math.max(1, track.duration * layout.fps),
                  trackLabel(track, narrationNumber.get(track.id) ?? 1),
                  collidingVoice.has(track.id)
                    ? "overlaps the line beside it"
                    : track.role === "head-voice" || track.role === "end-voice"
                      ? "drag to move"
                      : "",
                  !track.enabled ? "muted" : collidingVoice.has(track.id) ? "clash" : "voice",
                  false,
                  () => onSelectSection(SECTION_FOR_ROLE[track.role]),
                  undefined,
                  track.role === "head-voice"
                    ? (event) => dragVoice(event, "head")
                    : track.role === "end-voice"
                      ? (event) => dragVoice(event, "end")
                      : undefined,
                ),
              ),
            )}

            {timeline.audio.length === 0 ? (
              <span
                className="pointer-events-none absolute left-2 text-[10px] italic text-ink-400"
                style={{ top: laneTop.voice + 5 }}
              >
                Silent
              </span>
            ) : null}

            {/* Playhead: one line across every lane. */}
            <div
              className="pointer-events-none absolute w-px bg-ember-400"
              style={{ left: pct(frame), top: RULER_H, height: totalHeight - RULER_H }}
            >
              {/* The grab tab hangs below the line rather than above it: above, it
                  sat on top of the ruler's tick labels. */}
              <span className="absolute -left-[4px] top-0 h-[7px] w-[9px] rounded-b-[2px] bg-ember-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
